import { useCallback, useMemo } from 'react';
import { shallow } from 'zustand/shallow';
import { useStoreWithEqualityFn } from 'zustand/traditional';

import { getHSL } from '../../lib/string.js';
import { selectData, selectById } from '../selectors.ts';
import { useAttach } from '../useAttach';

import { logsStore, type LogsStoreState, type LogProps } from './LogsStore';

export function useLogsData() {
  useAttach(logsStore, 'useLogsData');
  return useStoreWithEqualityFn(logsStore.store, selectData, shallow);
}
useLogsData.store = logsStore;

export function useLogsById() {
  useAttach(logsStore, 'useLogsById');
  return useStoreWithEqualityFn(logsStore.store, selectById, shallow);
}
useLogsById.store = logsStore;

export function useLogsByOwnerUri() {
  const logs = useLogsById();

  return useMemo(() => {
    if (logs.byId == null) {
      return {
        byOwnerUri: null,
        isLoading: logs.isLoading,
        error: logs.error,
      };
    }

    const result: Record<string, Record<string, LogProps>> = {};

    for (const log of Object.values(logs.byId)) {
      result[log.ownerUri] = result[log.ownerUri] ?? {};
      result[log.ownerUri][log.id] = log;
    }

    return {
      byOwnerUri: result,
      isLoading: logs.isLoading,
      error: logs.error,
    };
  }, [logs.byId, logs.isLoading, logs.error]);
}
useLogsByOwnerUri.store = logsStore;

export function useLog({ logId }: { logId: string }) {
  useAttach(logsStore, 'useLog');

  const select = useCallback(
    (state: LogsStoreState) => {
      return {
        log: state.byId?.[logId] ?? null,
        logInstance: state.data?.[logId] ?? null,
        isLoading: state.isLoading,
        error: state.error,
      };
    },
    [logId],
  );

  return useStoreWithEqualityFn(logsStore.store, select, shallow);
}
useLog.store = logsStore;

export function useLogsForOwnerUri({ ownerUri }: { ownerUri: string }) {
  const logs = useLogsByOwnerUri();

  return {
    logs: logs.byOwnerUri?.[ownerUri] ?? null,
  };
}
useLogsForOwnerUri.store = logsStore;

export function useLogsAttach() {
  useAttach(logsStore, 'useLogsAttach');
}
useLogsAttach.store = logsStore;

export function useLogsColorMap() {
  const logs = useLogsData();

  const colorMap = useMemo(() => {
    const result: Record<string, { hue: number; hsl: string }> = {};

    for (const [index, log] of Object.values(logs.data ?? {}).entries()) {
      const hue = Math.round(5 * index) % 360;
      result[log.id] = {
        hue: hue,
        hsl: getHSL(hue),
      };
    }

    return result;
  }, [logs.data]);

  return {
    colorMap: colorMap,
  };
}
